import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../Toast';
import {
  GraduationCap, Save, Loader2, Building2, BookOpen, Banknote, Calendar, Users, FileText,
  CheckCircle2, XCircle, AlertTriangle, Sparkles, Download, RefreshCw, Target, Shield,
  TrendingUp, ClipboardCheck, Wrench, ExternalLink, Clock,
} from 'lucide-react';
import { crmApi } from '../../services/crmApi';
import { kfsApi } from '../../services/trainingsApi';

const TABS = [
  { id: 'company', label: 'Firma', icon: Building2, color: 'text-blue-600' },
  { id: 'training', label: 'Szkolenie', icon: BookOpen, color: 'text-violet-600' },
  { id: 'budget', label: 'Budżet & Terminy', icon: Banknote, color: 'text-emerald-600' },
  { id: 'documents', label: 'Dokumenty', icon: ClipboardCheck, color: 'text-amber-600' },
  { id: 'generator', label: 'Generator AI', icon: Sparkles, color: 'text-rose-600' },
  { id: 'tools', label: 'Narzędzia', icon: Wrench, color: 'text-orange-600' },
  { id: 'notes', label: 'Notatki', icon: FileText, color: 'text-sky-600' },
] as const;

type TabId = typeof TABS[number]['id'];

const COMPANY_SIZES: Record<string, string> = {
  mikro: 'Mikro (1-9)', mala: 'Mała (10-49)', srednia: 'Średnia (50-249)', duza: 'Duża (250+)',
};

const TRAINING_AREAS: Record<string, string> = {
  it: 'IT / Cyfryzacja', zarzadzanie: 'Zarządzanie', finanse: 'Finanse / Księgowość',
  sprzedaz: 'Sprzedaż / Marketing', produkcja: 'Produkcja / Jakość',
  bhp: 'BHP / Prawo pracy', jezyki: 'Języki obce', miękkie: 'Kompetencje miękkie',
  techniczne: 'Kompetencje techniczne', inne: 'Inne',
};

interface Props {
  application: any;
  onSaved: () => void;
}

export const KFSDetailSection: React.FC<Props> = ({ application, onSaved }) => {
  const app = application;
  const [activeTab, setActiveTab] = useState<TabId>('company');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fields, setFields] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!app) return;
    const f: Record<string, any> = {};
    Object.keys(app).forEach(k => { if (k.startsWith('kfs_')) f[k] = app[k]; });
    f._notes = app.notes || '';
    f._operator_notes = app.operator_notes || '';
    setFields(f);
    setDirty(false);
  }, [app?.id]);

  const set = (key: string, value: any) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      Object.entries(fields).forEach(([k, v]) => { if (k.startsWith('kfs_')) payload[k] = v; });
      payload.notes = fields._notes;
      payload.operator_notes = fields._operator_notes;
      await crmApi.updateApplication(app.id, payload);
      setDirty(false);
      onSaved();
    } catch (e) { console.error('Save KFS failed:', e); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: number }) => (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
  const Input = ({ value, onChange, type = 'text', placeholder = '' }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) => (
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
  );
  const Select = ({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Record<string, string> }) => (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent">
      <option value="">— wybierz —</option>
      {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
  const Textarea = ({ value, onChange, rows = 3, placeholder = '' }: { value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) => (
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
  );

  const budget = parseFloat(fields.kfs_estimated_budget) || 0;
  const participants = parseInt(fields.kfs_participant_count) || 0;
  const perPerson = participants > 0 ? Math.round(budget / participants) : 0;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-violet-50 to-indigo-50 px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap size={22} className="text-violet-600" />
            <h3 className="text-lg font-bold text-gray-900">KFS Szkolenia — szczegóły</h3>
          </div>
          <button onClick={handleSave} disabled={!dirty || saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${dirty ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Zapisz
          </button>
        </div>
        {(budget > 0 || participants > 0) && (
          <div className="mt-2 flex gap-4 text-sm">
            {budget > 0 && (
              <div className="bg-white/70 rounded-lg px-3 py-1.5">
                <span className="text-gray-500">Budżet: </span>
                <span className="font-bold text-violet-700">{budget.toLocaleString('pl-PL')} zł</span>
              </div>
            )}
            {participants > 0 && (
              <div className="bg-white/70 rounded-lg px-3 py-1.5">
                <span className="text-gray-500">Uczestników: </span>
                <span className="font-bold text-violet-700">{participants}</span>
              </div>
            )}
            {perPerson > 0 && (
              <div className="bg-white/70 rounded-lg px-3 py-1.5">
                <span className="text-gray-500">Na osobę: </span>
                <span className="font-bold text-violet-700">~{perPerson.toLocaleString('pl-PL')} zł</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex border-b bg-gray-50 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${activeTab === tab.id ? `${tab.color} border-current bg-white` : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </div>

      <div className="p-6">
        {activeTab === 'company' && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nazwa firmy"><Input value={fields.kfs_company_name} onChange={v => set('kfs_company_name', v)} /></Field>
            <Field label="NIP"><Input value={fields.kfs_company_nip} onChange={v => set('kfs_company_nip', v)} /></Field>
            <Field label="REGON"><Input value={fields.kfs_company_regon} onChange={v => set('kfs_company_regon', v)} /></Field>
            <Field label="Wielkość firmy"><Select value={fields.kfs_company_size} onChange={v => set('kfs_company_size', v)} options={COMPANY_SIZES} /></Field>
            <Field label="Adres" span={2}><Input value={fields.kfs_company_address} onChange={v => set('kfs_company_address', v)} /></Field>
            <Field label="Liczba pracowników"><Input value={fields.kfs_employee_count} onChange={v => set('kfs_employee_count', v)} type="number" /></Field>
            <Field label="Osoba kontaktowa"><Input value={fields.kfs_contact_position} onChange={v => set('kfs_contact_position', v)} /></Field>
          </div>
        )}

        {activeTab === 'training' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Obszar szkolenia"><Select value={fields.kfs_training_area} onChange={v => set('kfs_training_area', v)} options={TRAINING_AREAS} /></Field>
              <Field label="Liczba uczestników"><Input value={fields.kfs_participant_count} onChange={v => set('kfs_participant_count', v)} type="number" /></Field>
            </div>
            <Field label="Opis szkolenia" span={2}>
              <Textarea value={fields.kfs_training_description} onChange={v => set('kfs_training_description', v)} rows={4} placeholder="Szczegółowy opis szkolenia, zakres, cele..." />
            </Field>
            <Field label="Oczekiwane rezultaty" span={2}>
              <Textarea value={fields.kfs_expected_outcomes} onChange={v => set('kfs_expected_outcomes', v)} rows={3} placeholder="Jakie kompetencje/certyfikaty mają uzyskać uczestnicy?" />
            </Field>
          </div>
        )}

        {activeTab === 'budget' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Szacowany budżet (zł)"><Input value={fields.kfs_estimated_budget} onChange={v => set('kfs_estimated_budget', v)} type="number" /></Field>
              <Field label="Preferowane terminy"><Input value={fields.kfs_preferred_dates} onChange={v => set('kfs_preferred_dates', v)} placeholder="np. kwiecień–maj 2026" /></Field>
            </div>

            {budget > 0 && (
              <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
                <h4 className="text-sm font-medium text-violet-800 mb-2">Dofinansowanie KFS</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-violet-700">{Math.round(budget * 0.8).toLocaleString('pl-PL')} zł</div>
                    <div className="text-xs text-gray-500">Dofinansowanie (80%)</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-700">{Math.round(budget * 0.2).toLocaleString('pl-PL')} zł</div>
                    <div className="text-xs text-gray-500">Wkład firmy (20%)</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-emerald-700">{budget.toLocaleString('pl-PL')} zł</div>
                    <div className="text-xs text-gray-500">Łącznie</div>
                  </div>
                </div>
                <p className="text-xs text-violet-600 mt-2">
                  Mikroprzedsiębiorstwa: dofinansowanie do 100%. Pozostałe: 80% (max 300% przeciętnego wynagrodzenia na osobę).
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && <DocumentsTab applicationId={app.id} />}

        {activeTab === 'generator' && <GeneratorTab applicationId={app.id} />}

        {activeTab === 'tools' && <ToolsTab application={app} />}

        {activeTab === 'notes' && (
          <div className="space-y-4">
            <Field label="Notatki klienta" span={2}>
              <Textarea value={fields._notes} onChange={v => set('_notes', v)} rows={4} placeholder="Notatki z formularza..." />
            </Field>
            <Field label="Notatki operatorskie" span={2}>
              <Textarea value={fields._operator_notes} onChange={v => set('_operator_notes', v)} rows={4} placeholder="Notatki operatora — wewnętrzne..." />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Documents Tab ──────────────────────────────────────────

function DocumentsTab({ applicationId }: { applicationId: number }) {
  const [checklist, setChecklist] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await kfsApi.getChecklistStatus(applicationId);
      setChecklist(result);
    } catch { /* ignore */ }
    setLoading(false);
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-500" size={24} /></div>;

  return (
    <div className="space-y-4">
      {/* Progress */}
      {checklist && (
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-amber-800">Kompletność dokumentacji KFS</span>
            <span className={`text-lg font-bold ${checklist.complete ? 'text-emerald-700' : 'text-amber-700'}`}>
              {checklist.percentage}%
            </span>
          </div>
          <div className="w-full bg-amber-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${checklist.complete ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${checklist.percentage}%` }}
            />
          </div>
          <p className="text-xs text-amber-600 mt-1">
            {checklist.uploadedCount} z {checklist.totalRequired} wymaganych dokumentów
          </p>
        </div>
      )}

      {/* Checklist */}
      <div className="space-y-2">
        {checklist?.documents?.map((doc: any) => (
          <div key={doc.key} className={`flex items-center gap-3 p-3 rounded-lg border ${
            doc.uploaded ? 'bg-emerald-50 border-emerald-200' :
            doc.required ? 'bg-red-50 border-red-200' :
            'bg-gray-50 border-gray-200'
          }`}>
            {doc.uploaded ? (
              <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
            ) : doc.required ? (
              <XCircle size={18} className="text-red-500 shrink-0" />
            ) : (
              <AlertTriangle size={18} className="text-gray-400 shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">{doc.label}</p>
              {doc.ocrConfidence && (
                <p className="text-xs text-gray-400">OCR: {doc.ocrConfidence}% pewności</p>
              )}
            </div>
            {doc.required && !doc.uploaded && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Wymagany</span>
            )}
            {!doc.required && !doc.uploaded && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Opcjonalny</span>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 mt-4">
        Dokumenty są automatycznie klasyfikowane po OCR. Upload plików przez sekcję "Dokumenty" powyżej.
      </p>
    </div>
  );
}

// ─── Generator Tab ──────────────────────────────────────────

function GeneratorTab({ applicationId }: { applicationId: number }) {
  const [scoring, setScoring] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [scoreResult, setScoreResult] = useState<any>(null);
  const [packageResult, setPackageResult] = useState<any>(null);
  const [strategy, setStrategy] = useState<'A' | 'B' | 'C'>('A');

  const handleScore = async () => {
    setScoring(true);
    try {
      const result = await kfsApi.autoScoreApplication(applicationId);
      setScoreResult(result);
    } catch (e: any) { toast.error('Błąd analizy: ' + (e.response?.data?.error || e.message)); }
    setScoring(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await kfsApi.generatePackage(applicationId, strategy);
      setPackageResult(result);
    } catch (e: any) { toast.error('Błąd generowania: ' + (e.response?.data?.error || e.message)); }
    setGenerating(false);
  };

  return (
    <div className="space-y-6">
      {/* Auto-score */}
      <div className="bg-gradient-to-r from-rose-50 to-violet-50 rounded-lg p-4 border border-rose-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-bold text-rose-800 flex items-center gap-2">
              <Target size={16} /> Analiza automatyczna AI
            </h4>
            <p className="text-xs text-rose-600 mt-1">Dopasowanie priorytetu, de minimis, ocena szans</p>
          </div>
          <button onClick={handleScore} disabled={scoring}
            className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 disabled:opacity-50">
            {scoring ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {scoring ? 'Analizuję...' : 'Analizuj wniosek'}
          </button>
        </div>
      </div>

      {/* Score results */}
      {scoreResult && (
        <div className="space-y-4">
          {/* Overall score */}
          <div className="flex items-center gap-4">
            <div className={`text-3xl font-bold ${
              scoreResult.overallScore >= 70 ? 'text-emerald-600' :
              scoreResult.overallScore >= 40 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {scoreResult.overallScore}/100
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Ocena szans</p>
              <p className="text-xs text-gray-500">{scoreResult.companyName}</p>
            </div>
          </div>

          {/* Priority match */}
          <div className="bg-violet-50 rounded-lg p-3 border border-violet-200">
            <p className="text-xs font-medium text-violet-700">Dopasowany priorytet KFS 2026</p>
            <p className="text-sm font-bold text-violet-900">Nr {scoreResult.matchedPriority.nr}: {scoreResult.matchedPriority.name}</p>
          </div>

          {/* De minimis */}
          {scoreResult.deMinimis && (
            <div className={`rounded-lg p-3 border ${scoreResult.deMinimis.exceedsLimit ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <p className="text-xs font-medium text-gray-600 flex items-center gap-1">
                <Shield size={14} /> De minimis
              </p>
              <p className="text-sm">
                Wykorzystane: <strong>{scoreResult.deMinimis.currentTotal.toLocaleString('pl-PL')} EUR</strong> / {scoreResult.deMinimis.limitEur.toLocaleString('pl-PL')} EUR
                ({scoreResult.deMinimis.utilizationPercent}%)
              </p>
              {scoreResult.deMinimis.warning && (
                <p className="text-xs text-red-600 mt-1">{scoreResult.deMinimis.warning}</p>
              )}
            </div>
          )}

          {/* Funding */}
          {scoreResult.funding && (
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
              <p className="text-xs font-medium text-emerald-700">Dofinansowanie KFS ({scoreResult.funding.kfsPercent}%)</p>
              <div className="flex gap-4 mt-1">
                <div>
                  <p className="text-lg font-bold text-emerald-700">{scoreResult.funding.requestedFunding.toLocaleString('pl-PL')} zł</p>
                  <p className="text-xs text-gray-500">Dofinansowanie</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-600">{scoreResult.funding.ownContribution.toLocaleString('pl-PL')} zł</p>
                  <p className="text-xs text-gray-500">Wkład własny</p>
                </div>
              </div>
            </div>
          )}

          {/* Strengths & Weaknesses */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1"><TrendingUp size={14} /> Mocne strony</p>
              <ul className="space-y-1">
                {scoreResult.strengths?.map((s: string, i: number) => (
                  <li key={i} className="text-xs text-emerald-800 flex items-start gap-1">
                    <CheckCircle2 size={12} className="shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1"><AlertTriangle size={14} /> Ryzyka</p>
              <ul className="space-y-1">
                {scoreResult.weaknesses?.map((w: string, i: number) => (
                  <li key={i} className="text-xs text-amber-800 flex items-start gap-1">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recommendation */}
          {scoreResult.recommendation && (
            <div className="bg-gray-50 rounded-lg p-3 border">
              <p className="text-xs font-medium text-gray-500 mb-1">Rekomendacja AI</p>
              <p className="text-sm text-gray-700">{scoreResult.recommendation}</p>
            </div>
          )}
        </div>
      )}

      {/* Generator */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <FileText size={16} /> Generator pakietu KFS
        </h4>

        {/* Strategy selector */}
        <div className="space-y-2 mb-4">
          <p className="text-xs text-gray-500">Strategia argumentacji:</p>
          {[
            { key: 'A' as const, label: 'Strategia A', desc: 'Już kupiliśmy platformę AI — szkolimy z obsługi' },
            { key: 'B' as const, label: 'Strategia B', desc: 'Planujemy wdrożenie AI — szkolimy profilaktycznie' },
            { key: 'C' as const, label: 'Strategia C', desc: 'Mamy narzędzia ale nie wykorzystujemy AI' },
          ].map(s => (
            <label key={s.key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
              strategy === s.key ? 'bg-rose-50 border-rose-300' : 'bg-white border-gray-200 hover:border-gray-300'
            }`}>
              <input type="radio" name="strategy" value={s.key} checked={strategy === s.key}
                onChange={() => setStrategy(s.key)} className="mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-700">{s.label}</p>
                <p className="text-xs text-gray-500">{s.desc}</p>
              </div>
            </label>
          ))}
        </div>

        <button onClick={handleGenerate} disabled={generating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50">
          {generating ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
          {generating ? 'Generuję pakiet...' : 'Generuj pakiet KFS'}
        </button>
      </div>

      {/* Generated package preview */}
      {packageResult && (
        <div className="space-y-4 border-t pt-4">
          <h4 className="text-sm font-bold text-emerald-700 flex items-center gap-2">
            <CheckCircle2 size={16} /> Pakiet wygenerowany
          </h4>

          <div className="bg-white rounded-lg border p-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Uzasadnienie (Strategia {strategy})</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {packageResult.justification}
            </p>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Uzasadnienie priorytetu KFS</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {packageResult.priorityJustification}
            </p>
          </div>

          {packageResult.costCalculation && (
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
              <p className="text-xs font-medium text-emerald-700 mb-2">Kalkulacja kosztów</p>
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div>
                  <p className="font-bold text-emerald-700">{packageResult.costCalculation.requestedFunding.toLocaleString('pl-PL')} zł</p>
                  <p className="text-xs text-gray-500">Dofinansowanie ({packageResult.costCalculation.kfsPercent}%)</p>
                </div>
                <div>
                  <p className="font-bold text-gray-700">{packageResult.costCalculation.ownContribution.toLocaleString('pl-PL')} zł</p>
                  <p className="text-xs text-gray-500">Wkład własny ({packageResult.costCalculation.ownPercent}%)</p>
                </div>
                <div>
                  <p className="font-bold text-violet-700">{packageResult.costCalculation.totalExpenses.toLocaleString('pl-PL')} zł</p>
                  <p className="text-xs text-gray-500">Łącznie</p>
                </div>
              </div>
            </div>
          )}

          {packageResult.deMinimisReport && (
            <div className="bg-gray-50 rounded-lg border p-3 text-xs text-gray-600">
              De minimis: {packageResult.deMinimisReport.currentTotal.toLocaleString('pl-PL')} EUR wykorzystane
              (pozostało {packageResult.deMinimisReport.remainingEur.toLocaleString('pl-PL')} EUR)
            </div>
          )}

          <button onClick={() => {
            const text = `UZASADNIENIE SZKOLENIA KFS\n\n${packageResult.justification}\n\n---\n\nUZASADNIENIE PRIORYTETU\n\n${packageResult.priorityJustification}`;
            navigator.clipboard.writeText(text);
            toast.success('Skopiowano do schowka!');
          }} className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800">
            <ClipboardCheck size={16} /> Kopiuj do schowka
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tools Tab (BUR pricing, scheduling, priorities, justification) ──────────

const PRZECIETNE_WYNAGRODZENIE = 8903.56;

const BUR_PRICE_HINTS = [
  { cat: 'Marketing / PR / Sprzedaż', median: 95, q3: 145 },
  { cat: 'Zarządzanie przedsiębiorstwem', median: 120, q3: 180 },
  { cat: 'Zarządzanie zasobami ludzkimi / HR', median: 110, q3: 165 },
  { cat: 'Programowanie / IT', median: 150, q3: 220 },
  { cat: 'Bezpieczeństwo IT / cybersecurity', median: 160, q3: 240 },
  { cat: 'Sztuczna inteligencja / AI', median: 170, q3: 260 },
  { cat: 'Rachunkowość / Finanse / Podatki', median: 100, q3: 155 },
  { cat: 'Prawo pracy / administracyjne', median: 115, q3: 175 },
  { cat: 'Techniczne / inżynieryjne', median: 85, q3: 130 },
  { cat: 'Zdrowie / medycyna / opieka', median: 90, q3: 140 },
  { cat: 'Języki obce', median: 65, q3: 100 },
  { cat: 'Ekologia / zielone kompetencje', median: 105, q3: 160 },
];

const HOLIDAYS_2026 = [
  { date: '2026-01-01', name: 'Nowy Rok' }, { date: '2026-01-06', name: 'Trzech Króli' },
  { date: '2026-04-05', name: 'Wielkanoc' }, { date: '2026-04-06', name: 'Pon. Wielkanocny' },
  { date: '2026-05-01', name: 'Święto Pracy' }, { date: '2026-05-03', name: 'Konstytucja 3 Maja' },
  { date: '2026-05-24', name: 'Zielone Świątki' }, { date: '2026-06-04', name: 'Boże Ciało' },
  { date: '2026-08-15', name: 'Wniebowzięcie NMP' }, { date: '2026-11-01', name: 'Wszystkich Świętych' },
  { date: '2026-11-11', name: 'Święto Niepodległości' }, { date: '2026-12-25', name: 'Boże Narodzenie' },
  { date: '2026-12-26', name: 'Boże Narodzenie 2' },
];

const INDUSTRY_PEAKS = [
  { months: [1,2,3,4], industry: 'Księgowość / Finanse', reason: 'Zamknięcia roczne, PIT-y, bilanse', severity: 'high' },
  { months: [5,6,7,8,9], industry: 'Budownictwo', reason: 'Sezon budowlany — trudno oderwać pracowników', severity: 'high' },
  { months: [12,1], industry: 'Handel / Retail', reason: 'Święta + wyprzedaże', severity: 'high' },
  { months: [6,7,8], industry: 'Turystyka / HoReCa', reason: 'Sezon letni', severity: 'high' },
  { months: [5,6,9,10], industry: 'Fryzjerstwo / Uroda', reason: 'Śluby, komunie, studniówki', severity: 'medium' },
  { months: [3,4], industry: 'Rolnictwo', reason: 'Wiosenne prace polowe', severity: 'medium' },
  { months: [9], industry: 'Edukacja', reason: 'Początek roku szkolnego', severity: 'medium' },
  { months: [1,6,12], industry: 'Transport / Logistyka', reason: 'Szczyty sezonowe', severity: 'medium' },
];

const PRIORYTETY_KFS = [
  { id: 'min1', type: 'MIN', label: 'Zarządzanie + przeciwdziałanie mobbingowi', color: 'bg-blue-100 text-blue-700' },
  { id: 'min2', type: 'MIN', label: 'Zawody deficytowe (Barometr 2026)', color: 'bg-blue-100 text-blue-700' },
  { id: 'min3', type: 'MIN', label: 'Kompetencje cyfrowe, AI, zielone', color: 'bg-blue-100 text-blue-700' },
  { id: 'min4', type: 'MIN', label: 'Ochrona zdrowia / pomoc społeczna', color: 'bg-blue-100 text-blue-700' },
  { id: 'wup1', type: 'WUP', label: 'Pracownicy 45+ lat', color: 'bg-green-100 text-green-700' },
  { id: 'wup2', type: 'WUP', label: 'Powracający do pracy (opieka/rodzina)', color: 'bg-green-100 text-green-700' },
  { id: 'wup3', type: 'WUP', label: 'Osoby z niepełnosprawnościami', color: 'bg-green-100 text-green-700' },
  { id: 'gup1', type: 'GUP', label: 'Przekwalifikowanie zagrożonych zwolnieniem', color: 'bg-amber-100 text-amber-700' },
];

const JUSTIFICATION_TEMPLATES: Record<string, string> = {
  min1: 'Szkolenie z zakresu zarządzania i przeciwdziałania mobbingowi jest niezbędne dla kadry kierowniczej firmy. Podniesienie kompetencji menedżerskich pozwoli na skuteczniejsze zarządzanie zespołem, poprawę komunikacji wewnętrznej oraz wdrożenie procedur antymobbingowych.',
  min2: 'Szkolenie dotyczy zawodu deficytowego wskazanego w Barometrze Zawodów 2026 dla powiatu krakowskiego. Podniesienie kwalifikacji pracowników odpowiada na realne zapotrzebowanie lokalnego rynku pracy.',
  min3: 'Szkolenie z kompetencji cyfrowych/AI/zielonych odpowiada na wyzwania transformacji cyfrowej i ekologicznej. Pracownicy potrzebują nowych umiejętności, aby efektywnie korzystać z nowoczesnych narzędzi.',
  min4: 'Szkolenie dla pracowników sektora ochrony zdrowia jest konieczne dla utrzymania i podniesienia jakości usług. Ciągły rozwój wiedzy medycznej wymaga systematycznego kształcenia personelu.',
  wup1: 'Szkolenie skierowane do pracowników w wieku 45 lat i więcej, wymagających aktualizacji kompetencji w dynamicznym środowisku pracy. Inwestycja zapobiega wykluczeniu z rynku pracy.',
  wup2: 'Szkolenie dedykowane osobom powracającym do pracy po przerwie związanej z opieką nad dzieckiem. Aktualizacja wiedzy umożliwi pełny powrót do aktywności zawodowej.',
  wup3: 'Szkolenie dla osób z niepełnosprawnościami, potrzebujących dostosowanego kształcenia ustawicznego. Zwiększa szanse na utrzymanie zatrudnienia i awans zawodowy.',
  gup1: 'Szkolenie skierowane do pracowników zagrożonych utratą zatrudnienia w wyniku zmian organizacyjnych/technologicznych. Przekwalifikowanie zapobiegnie bezrobociu.',
};

function ToolsTab({ application }: { application: any }) {
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [justification, setJustification] = useState('');
  const [checkMonth, setCheckMonth] = useState(new Date().getMonth() + 1);

  const meta = (() => {
    try { return typeof application.notes === 'string' ? JSON.parse(application.notes) : (application.notes || {}); } catch { return {}; }
  })();

  const participants = meta?.employees_count || application.kfs_participant_count || 0;
  const fte = parseFloat(meta?.employee_fte || application.kfs_employee_count || '0');
  const isMikro = fte < 10;
  const kfsPercent = isMikro ? 90 : 70;
  const annualLimit = (isMikro ? 4 : fte < 50 ? 8 : fte < 250 ? 12 : 14) * PRZECIETNE_WYNAGRODZENIE;
  const perPersonLimit = 2 * PRZECIETNE_WYNAGRODZENIE;

  const peaksForMonth = INDUSTRY_PEAKS.filter(p => p.months.includes(checkMonth));

  return (
    <div className="space-y-6">
      {/* KFS Timeline */}
      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
        <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2 mb-3">
          <Calendar size={16} /> Kalendarz KFS Kraków 2026
        </h4>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {[
            { date: '13.04', label: 'Początek naboru', cls: 'bg-green-100 text-green-700 border-green-300' },
            { date: '17.04', label: 'Koniec naboru', cls: 'bg-green-100 text-green-700 border-green-300' },
            { date: '18.04', label: 'Rozpatrywanie (60 dni)', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
            { date: '17.06', label: 'Termin decyzji PUP', cls: 'bg-red-100 text-red-700 border-red-300' },
          ].map((t, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="w-4 h-0.5 bg-gray-300" />}
              <div className={`px-2 py-1 rounded-lg border font-medium ${t.cls}`}>
                <span className="font-mono">{t.date}</span> — {t.label}
              </div>
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-orange-600 mt-2 flex items-center gap-1"><Clock size={12} /> Budżet GUP Kraków: 14 109 100 PLN</p>
      </div>

      {/* Limity KFS */}
      <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
        <h4 className="text-sm font-bold text-violet-800 mb-2">Limity KFS dla tego wnioskodawcy</h4>
        <div className="grid grid-cols-4 gap-3 text-center text-xs">
          <div className="bg-white rounded-lg p-2 border">
            <p className="font-bold text-lg text-violet-700">{kfsPercent}%</p>
            <p className="text-gray-500">Dofinansowanie</p>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <p className="font-bold text-lg text-violet-700">{(100 - kfsPercent)}%</p>
            <p className="text-gray-500">Wkład własny</p>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <p className="font-bold text-sm text-violet-700">{Math.round(annualLimit).toLocaleString('pl-PL')} zł</p>
            <p className="text-gray-500">Limit roczny</p>
          </div>
          <div className="bg-white rounded-lg p-2 border">
            <p className="font-bold text-sm text-violet-700">{Math.round(perPersonLimit).toLocaleString('pl-PL')} zł</p>
            <p className="text-gray-500">Na osobę (max)</p>
          </div>
        </div>
        <p className="text-xs text-violet-600 mt-2">
          {isMikro ? 'Mikroprzedsiębiorstwo' : fte < 50 ? 'Małe' : fte < 250 ? 'Średnie' : 'Duże'} ({fte} etatów, {participants} uczestników)
        </p>
      </div>

      {/* BUR Pricing Reference */}
      <div className="bg-white rounded-lg p-4 border">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
            <TrendingUp size={16} /> Referencja cenowa BUR (mediana Q4 2025)
          </h4>
          <a href="https://uslugirozwojowe.parp.gov.pl/wyszukiwarka/porownywarka" target="_blank" rel="noopener noreferrer"
            className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1">
            Porównywarka BUR <ExternalLink size={12} />
          </a>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {BUR_PRICE_HINTS.map((p, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
              <span className="text-gray-700">{p.cat}</span>
              <div className="flex gap-3 font-mono">
                <span className="text-green-700 font-medium">{p.median} zł</span>
                <span className="text-gray-400">Q3: {p.q3} zł</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-gray-400 mt-2">PLN netto / osobogodzinę. Ceny bliskie mediany zwiększają szanse na pozytywną ocenę.</p>
      </div>

      {/* Smart Scheduling */}
      <div className="bg-white rounded-lg p-4 border">
        <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3">
          <Calendar size={16} /> Planowanie terminów szkolenia
        </h4>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-500">Sprawdź miesiąc:</span>
          <select value={checkMonth} onChange={e => setCheckMonth(parseInt(e.target.value))}
            className="border rounded-lg px-2 py-1 text-sm">
            {['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'].map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>

        {/* Holidays in month */}
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-600 mb-1">Święta w wybranym miesiącu:</p>
          {HOLIDAYS_2026.filter(h => parseInt(h.date.split('-')[1]) === checkMonth).length > 0 ? (
            <div className="space-y-1">
              {HOLIDAYS_2026.filter(h => parseInt(h.date.split('-')[1]) === checkMonth).map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-red-50 text-red-700 rounded-lg px-2 py-1">
                  <AlertTriangle size={12} /> {h.date.split('-').reverse().join('.')} — {h.name}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> Brak świąt w tym miesiącu</p>
          )}
        </div>

        {/* Industry peaks */}
        <p className="text-xs font-medium text-gray-600 mb-1">Okresy wzmożonej pracy w branżach:</p>
        {peaksForMonth.length > 0 ? (
          <div className="space-y-1">
            {peaksForMonth.map((p, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1 ${p.severity === 'high' ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-700'}`}>
                <AlertTriangle size={12} /> <strong>{p.industry}</strong>: {p.reason}
                {p.severity === 'high' && <span className="text-red-600 font-medium">(wysoki wpływ)</span>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> Brak istotnych konfliktów branżowych</p>
        )}
      </div>

      {/* Priority Justification Generator */}
      <div className="bg-white rounded-lg p-4 border">
        <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3">
          <Sparkles size={16} /> Generator uzasadnień per priorytet
        </h4>
        <p className="text-xs text-gray-500 mb-2">Zaznacz priorytety, które spełniają uczestnicy → wygeneruj uzasadnienie do wniosku.</p>
        <div className="space-y-1 mb-3">
          {PRIORYTETY_KFS.map(pr => (
            <label key={pr.id} className="flex items-start gap-2 text-xs p-1.5 rounded hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selectedPriorities.includes(pr.id)}
                onChange={e => setSelectedPriorities(prev => e.target.checked ? [...prev, pr.id] : prev.filter(x => x !== pr.id))}
                className="rounded mt-0.5" />
              <span><span className={`inline-block px-1 py-0 rounded text-[10px] font-medium mr-1 ${pr.color}`}>{pr.type}</span>{pr.label}</span>
            </label>
          ))}
        </div>
        {selectedPriorities.length > 0 && (
          <>
            <button onClick={() => {
              const text = selectedPriorities.map(pid => JUSTIFICATION_TEMPLATES[pid]).filter(Boolean).join('\n\n');
              setJustification(text);
            }} className="flex items-center gap-2 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-lg text-xs hover:bg-violet-200 mb-2">
              <Sparkles size={14} /> Generuj szablon uzasadnienia
            </button>
            <textarea value={justification} onChange={e => setJustification(e.target.value)} rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Uzasadnienie..." />
            <button onClick={() => { navigator.clipboard.writeText(justification); toast.success('Skopiowano!'); }}
              className="mt-2 flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800">
              <ClipboardCheck size={14} /> Kopiuj do schowka
            </button>
          </>
        )}
      </div>
    </div>
  );
}
